<template>
    <div ref="svgWrapperRef" class="svg-wrapper" :style="{ paddingTop: `${svgWrapperPaddingTop}px` }">
        <svg :width="width" :height="svgHeight" class="histogram-chart">
            <!-- 눈금선 (전체 ticks + 중심 포함) -->
            <g v-for="tick in fullTicks" :key="'tick-line-' + tick">
                <line
                    :x1="xScale(tick)"
                    :x2="xScale(tick)"
                    :y1="barAreaHeight"
                    :y2="barAreaHeight + tickLineHeight"
                    stroke="#999"
                />
            </g>

            <!-- 라벨 텍스트 (min/mid/max만) -->
            <g v-for="tick in labeledTicks" :key="'tick-label-' + tick">
                <text
                    :x="xScale(tick)"
                    :y="barAreaHeight + tickLineHeight + 13"
                    font-size="10"
                    :text-anchor="getTextAnchor(tick)"
                    :dx="getLabelDx(tick)"
                    fill="#666"
                >
                    {{ tick + (props.data.XAxis?.Unit ?? '') }}
                </text>
            </g>

            <!-- Histogram Bars -->
            <g v-for="(bar, i) in barList" :key="i">
                <rect
                    :x="xScale((bar.UnderVal + bar.UpperVal) / 2) - barWidth / 2"
                    :y="Math.max(0, barAreaHeight - yScale(bar.Count))"
                    :width="barWidth"
                    :height="yScale(bar.Count)"
                    :fill="bar.Color"
                />
            </g>

            <!-- x\u축 기준선: 전체 width -->
            <line
                x1="0"
                :x2="width"
                :y1="barAreaHeight + tickLineHeight - 5"
                :y2="barAreaHeight + tickLineHeight - 5"
                stroke="#999"
            />

            <!-- Auxiliary Lines -->
            <g v-for="(line, i) in auxiliaryLines" :key="'line-' + i">
                <!-- 보조선: bar 영역 기준 아래쪽 80% 위치 -->
                <line
                    :x1="xScale(line.X_Value)"
                    :x2="xScale(line.X_Value)"
                    :y1="barAreaHeight * 0.5"
                    :y2="barAreaHeight"
                    stroke="#999"
                    stroke-dasharray="4"
                />
                <!-- 텍스트: 선 위쪽에 마진 10 줌 -->
                <text
                    :x="xScale(line.X_Value)"
                    :y="barAreaHeight * 0.5 - 4"
                    font-size="10"
                    fill="#666"
                    text-anchor="middle"
                >
                    {{ line.Label }}
                </text>
            </g>

            <!-- User 기준선 -->
            <g v-if="userBar" class="user-rank-text-wrap" ref="rankTextRef">
                <!-- 기준선: 전체 바 중 가장 높은 높이만큼만 -->
                <line
                    :x1="xScale(userBarCenter)"
                    :x2="xScale(userBarCenter)"
                    :y1="barAreaHeight - yScale(maxCount)"
                    :y2="barAreaHeight"
                    :stroke="userBar?.Color"
                    stroke-dasharray="4"
                />

                <!-- 사용자 텍스트: Rank (bold) + X_Value (normal) -->
                <text
                    :x="adjustedUserBarCenterX"
                    :y="barAreaHeight - yScale(maxCount) - 8"
                    text-anchor="middle"
                    :fill="userBar?.Color"
                >
                    <tspan class="big-text">
                        {{ props.data.UserData?.Rank }}
                    </tspan>
                    <tspan class="small-text" dx="6">
                        {{ props.data.UserData?.X_Value + (props.data.XAxis?.Unit ?? '') }}
                    </tspan>
                </text>
            </g>
        </svg>
    </div>
</template>

<script lang="ts" setup>
import type { VarianceHistogramResDto } from '@common/openapi-client/openapi';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

const props = defineProps<{
    data: VarianceHistogramResDto;
    screenWidth: number;
}>();

let baseBarAreaHeight = 95;
const tickLineHeight = 5;
const tickLabelHeight = 13 + 4;

const width = props.screenWidth ? props.screenWidth : window.innerWidth ? window.innerWidth : 400;

const barList = computed(() =>
    (props.data.HistogramBarList ?? []).map(bar => ({
        UnderVal: bar.UnderVal ?? 0,
        UpperVal: bar.UpperVal ?? 0,
        Count: bar.Count ?? 0,
        Color: bar.Color ?? '',
        IsUserBar: bar.IsUserBar ?? false,
    })),
);
const auxiliaryLines = computed(() =>
    (props.data.AuxiliaryLines ?? []).map(line => ({
        X_Value: line.X_Value ?? 0,
        Label: line.Label ?? '',
    })),
);

const svgWrapperRef = ref<HTMLDivElement | null>(null);
const rankTextRef = ref<SVGTextElement | null>(null);

const svgWrapperPaddingTop = ref('0px');
const userTextWidth = ref(0);

const xMin = props.data.XAxis?.Min ?? 0;
const xMax = props.data.XAxis?.Max ?? 0;
const xMid = (xMin + xMax) / 2;

const userBar = computed(() => barList.value.find(bar => bar.IsUserBar));
const maxCount = computed(() => Math.max(...barList.value.map(bar => bar.Count)));

const extraHeight = computed(() => {
    return userBarHeight.value > 72 ? Math.ceil(userBarHeight.value - 72) : 0;
});

watch(
    () => props.data.UserData?.Rank,
    () => {
        applyDynamicPadding();
    },
);

watch([userBar, maxCount], () => {
    adjustBaseBarAreaHeight();
});

const userTextHeight = ref(0);

const adjustedUserBarCenterX = computed(() => {
    const rawX = xScale(userBarCenter.value);

    if (userTextWidth.value === 0) return rawX;

    const halfTextWidth = userTextWidth.value / 2;

    if (rawX - halfTextWidth < 0) {
        // 왼쪽 벗어남
        return halfTextWidth + 4; // +4는 살짝 여유
    } else if (rawX + halfTextWidth > width) {
        // 오른쪽 벗어남
        return width - halfTextWidth - 4; // -4 여유
    }
    return rawX; // 정상
});

const updateUserTextHeight = () => {
    if (rankTextRef.value) {
        const bbox = rankTextRef.value.getBBox();
        userTextHeight.value = bbox.height;
    }
};

const applyDynamicPadding = () => {
    // 텍스트가 그래프 영역을 나가면 paddingTop으로 설정
    if (!rankTextRef.value || !svgWrapperRef.value) return;

    const bbox = rankTextRef.value.getBBox();
    const topY = bbox.y; // y는 텍스트의 top 기준 (음수일 수 있음)

    const visibleHeight = Math.abs(topY); // SVG 내부에서 위로 튀어나간 높이

    if (visibleHeight > 0) {
        svgWrapperPaddingTop.value = `${visibleHeight}`;
    } else {
        svgWrapperPaddingTop.value = '0';
    }
};

const adjustBaseBarAreaHeight = () => {
    updateUserTextHeight();

    const barMaxHeight = yScale(maxCount.value, fixedBaseHeight);
    const textY = baseBarAreaHeight - barMaxHeight - userTextHeight.value - 5;

    const marginTopMin = 10;
    if (textY < marginTopMin) {
        const diff = marginTopMin - textY;
        baseBarAreaHeight += diff;
    }
};

const userBarCenter = computed(() => {
    if (!userBar.value) return 0;
    return (userBar.value.UnderVal + userBar.value.UpperVal) / 2;
});

const getLabelDx = (tick: number): number => {
    const threshold = 0.15;
    const padding = 8; // ⬅️ 이 값은 픽셀 단위로 조절 가능

    if (Math.abs(tick - xMin) < threshold) return padding; // Min: 오른쪽으로
    if (Math.abs(tick - xMax) < threshold) return -padding; // Max: 왼쪽으로
    return 0; // Mid: 그대로
};

const fullTicks = computed(() => {
    const min = props.data.XAxis?.Min ?? 0;
    const max = props.data.XAxis?.Max ?? 0;
    const tickCount = 11; // 항상 홀수여야 중심이 가운데
    const range = max - min;
    const step = range / (tickCount - 1);

    const ticks = [];
    for (let i = 0; i < tickCount; i++) {
        ticks.push(parseFloat((min + i * step).toFixed(3)));
    }

    return ticks;
});

const labeledTicks = computed(() => {
    const min = props.data.XAxis?.Min ?? 0;
    const max = props.data.XAxis?.Max ?? 0;
    const mid = (min + max) / 2;
    return [min, mid, max].map(v => parseFloat(v.toString()));
});

const getTextAnchor = (tick: number): 'start' | 'middle' | 'end' => {
    const threshold = 0.15;
    if (Math.abs(tick - xMin) < threshold) return 'start';
    if (Math.abs(tick - xMid) < threshold) return 'middle';
    if (Math.abs(tick - xMax) < threshold) return 'end';
    return 'middle';
};

const barGap = 2;
const xScale = (val: number) =>
    barGap +
    ((val - (props.data.XAxis?.Min ?? 0)) / ((props.data.XAxis?.Max ?? 0) - (props.data.XAxis?.Min ?? 0))) *
        (width - barGap * 2);

const fixedBaseHeight = 95;
const yScale = (count: number, height = baseBarAreaHeight) => {
    if (maxCount.value === 0) return 3;
    const scaled = (count / maxCount.value) * (height - 20);
    return scaled + 3;
};

const userBarHeight = computed(() => {
    if (!userBar.value) return 0;
    // 여기선 baseBarAreaHeight 말고 고정값으로 계산
    return yScale(userBar.value.Count, fixedBaseHeight);
});

const barAreaHeight = computed(() => baseBarAreaHeight + extraHeight.value);
const svgHeight = computed(() => barAreaHeight.value + tickLineHeight + tickLabelHeight);

const barWidth = computed(() => {
    if (barList.value.length < 2) return 5;

    const firstCenter = (barList.value[0].UnderVal + barList.value[0].UpperVal) / 2;
    const secondCenter = (barList.value[1].UnderVal + barList.value[1].UpperVal) / 2;

    return xScale(secondCenter) - xScale(firstCenter) - barGap;
});

onMounted(() => {
    if (rankTextRef.value) {
        const bbox = rankTextRef.value.getBBox();
        userTextWidth.value = bbox.width;
    }
    nextTick(() => {
        requestAnimationFrame(() => {
            applyDynamicPadding();
        });
    });
});
</script>

<style scoped>
.histogram-chart {
    overflow: visible;
}

.user-rank-text-wrap {
    .big-text {
        font-size: 1.4rem;
        text-anchor: middle;
        font-weight: bold;
        line-height: 130%;
    }

    .small-text {
        font-size: 1.1rem;
        text-anchor: middle;
        font-weight: normal;
        line-height: 130%;
    }
}
</style>
